import React, {
  memo,
  useMemo,
  useRef,
} from 'react';
import {
  LedgerRow,
} from 'pages/Dashboard/pages/Ledger/types';
import Tag from 'core/components/Tag';
import InsuranceChip from 'pages/Dashboard/components/InsuranceChips';
import ToolTip from 'core/components/Tooltip';
import {
  isEmptyOrDash,
} from 'pages/Dashboard/pages/Ledger/helper';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import {
  useGetWriteOffs,
} from 'pages/Dashboard/pages/Billing/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  EncounterPatientWriteOffDTO,
} from 'dtos';
import Table from 'core/components/VirtualTable';
import {
  ColumnDef,
} from '@tanstack/react-table';
import {
  formatDate,
} from 'utils/date';
import Name from 'pages/Dashboard/components/Name';
import dayjs from 'utils/dayjs';
import {
  formatMoney,
  isEmptyString,
} from '@populateui/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import ServiceLineStatusBadge from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ServiceLineStatusBadge';

const nullDate = dayjs('0001-01-01T00:00:00');
const eobLedgerCategories = new Set(['EOB', 'ERA']);

const columns: ColumnDef<EncounterPatientWriteOffDTO>[] = [
  {
    id: 'reason',
    accessorKey: 'reason',
    header: 'Reason',
    meta: {
      autoSized: true,
    },
  },
  {
    id: 'writeOffAmount',
    accessorKey: 'writeOffAmount',
    size: 80,
    header: 'Amount',
    cell: ({ row: { original } }) => <div>{formatMoney(original?.writeOffAmount ?? 0)}</div>,
  },
  {
    id: 'createdBy',
    accessorKey: 'createdBy',
    size: 180,
    header: 'Created By',
    cell: ({ row: { original } }) => (
      <Name
        name={getDisplayName(original?.createdByUser?.formattedNames, DEFAULT_EMPTY_VALUE)}
      />
    ),
  },
  {
    id: 'createdOn',
    accessorKey: 'createdOn',
    size: 100,
    header: 'Created On',
    cell: ({ row: { original } }) => {
      const isNullDate = dayjs(original?.createdOn).isSame(nullDate);
      return (<div>{isNullDate ? '--' : formatDate(original?.createdOn ?? '')}</div>);
    },
  },
];

const userPaidCategories = new Set(['Copay Paid', 'Patient Payment', 'Custom Charge']);

type InsuranceWriteOffServiceInfoProps = {
  id?: string | number;
  service?: string | null;
  details?: string | null;
  performedByUserName?: string | null;
};

type TooltipRowProps = {
  label: string;
  value: string;
};

function TooltipRow({
  label,
  value,
}: TooltipRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="font-semibold">{label}</div>
      <div className="text-gray-500">{value}</div>
    </div>
  );
}

function InsuranceWriteOffServiceInfo({
  id,
  service,
  details,
  performedByUserName,
}: InsuranceWriteOffServiceInfoProps) {
  const serviceInfoRef = useRef<HTMLSpanElement>(null);
  const serviceText = useMemo(
    () => (isEmptyOrDash(service) ? '' : service ?? ''),
    [service],
  );
  const performedByText = useMemo(
    () => (isEmptyOrDash(performedByUserName) ? '' : performedByUserName ?? ''),
    [performedByUserName],
  );

  const serviceInfoText = useMemo(() => compact([
    isEmptyString(serviceText) ? null : serviceText,
    isEmptyString(performedByText) ? null : performedByText,
  ]).join(' • '), [performedByText, serviceText]);

  const detailsText = useMemo(
    () => (isEmptyOrDash(details) ? '' : details ?? ''),
    [details],
  );
  const tooltipTitle = (
    <div className="flex flex-col gap-2 p-1 max-w-[450px]">
      {!isEmptyString(serviceText) && (
        <TooltipRow
          label="Type"
          value={serviceText}
        />
      )}
      {!isEmptyString(performedByText) && (
        <TooltipRow
          label="Created By"
          value={performedByText}
        />
      )}
      {!isEmptyString(detailsText) && (
        <TooltipRow
          label="Details"
          value={detailsText}
        />
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-0.5 text-gray-500 min-w-0">
      {!isEmptyString(serviceInfoText) && (
        <ToolTip
          title={tooltipTitle}
          ref={serviceInfoRef}
        >
          <span
            key={`${id}-service-info`}
            ref={serviceInfoRef}
            className="truncate block max-w-full"
          >
            {serviceInfoText}
          </span>
        </ToolTip>
      )}
    </div>
  );
}

function ServiceCell({
  isTransaction,
  category,
  performedByUserName,
  payer,
  service,
  details,
  id,
  encounterId,
  retirementReason,
}: LedgerRow) {
  const isInsuranceWriteOff = category === 'Write Off' && (service ?? '').includes('Insurance Resolution');
  const isRefundOrMoveToCredit = category === 'Moved to Credit' || category === 'Refund';
  const isCreditAllocation = category === 'Credit Allocation';
  const shouldIncludePerformedBy = (
    isInsuranceWriteOff
    || isRefundOrMoveToCredit
    || isCreditAllocation
  ) && !isNil(performedByUserName) && !isEmptyString(performedByUserName);

  const serviceInfoText = useMemo(() => compact([
    isEmptyOrDash(service) ? null : service,
    isEmptyOrDash(details) ? null : details,
    shouldIncludePerformedBy ? performedByUserName : null,
  ]).join(' • '), [service, details, performedByUserName, shouldIncludePerformedBy]);

  const serviceInfoRef = useRef<HTMLSpanElement>(null);

  const { data: writeOffs = [], refetch, isLoading } = useGetWriteOffs(encounterId ?? 0, false);

  const isUserPaid = useMemo(() => userPaidCategories.has(category ?? ''), [category]);
  const isPatientWriteOff = category === 'Write Off' && !service?.includes('Insurance Resolution');
  const shouldShowRetirementReason = isTransaction && eobLedgerCategories.has(category ?? '');

  const handleOnMouseEnter = () => {
    if (isPatientWriteOff && writeOffs.length === 0) {
      refetch?.();
    }
  };

  return isTransaction ? (
    <div className="flex flex-col gap-1 font-semibold overflow-hidden">
      <div className="flex gap-1 items-center">
        <Tag
          onMouseEnter={isPatientWriteOff ? handleOnMouseEnter : undefined}
          tooltip={isPatientWriteOff ? (
            <div className="p-2">
              {isLoading && (
                <div className="flex justify-center">
                  <Spinner />
                </div>
              )}
              {!isLoading && writeOffs?.length > 0 && (
                <Table<EncounterPatientWriteOffDTO>
                  data={writeOffs ?? []}
                  getRowId={(row) => row.encounterPatientWriteOffId}
                  columns={columns}
                  classes={{
                    header: '!border-0',
                    body: '!pb-0',
                    scrollContainer: '!pb-0 text-gray-600',
                  }}
                />
              )}
            </div>
          ) : undefined}
          className="font-semibold text-xs !w-fit !whitespace-nowrap"
          defaultColor="!bg-indigo-100 !text-indigo-700"
          tagName={category ?? ''}
        />
        {!isUserPaid && !isNil(payer) && (
          <span className="truncate max-w-fit">
            <InsuranceChip
              insuranceName={payer}
              tooltip={{
                title: (
                  <div className="flex flex-col gap-1">
                    <div className="font-semibold">Payer</div>
                    <div className="text-gray-500">{payer}</div>
                  </div>
                ),
                placement: 'right',
              }}
            />
          </span>
        )}
      </div>
      {isInsuranceWriteOff ? (
        <InsuranceWriteOffServiceInfo
          id={id}
          service={service}
          details={details}
          performedByUserName={performedByUserName}
        />
      ) : (
        <div className="flex items-center gap-1 text-gray-500 min-w-0">
          <ToolTip title={serviceInfoText} auto ref={serviceInfoRef}>
            <span
              key={`${id}-service-info`}
              ref={serviceInfoRef}
              className="truncate block max-w-full"
            >
              {serviceInfoText}
            </span>
          </ToolTip>
          {shouldShowRetirementReason && (
            <ServiceLineStatusBadge data={{ retirementReason }} />
          )}
        </div>
      )}
    </div>
  ) : <span>--</span>;
}

export default memo(ServiceCell);
